import React, { Component } from 'react'
import axios from 'axios'
import { Redirect } from 'react-router-dom'

export const UserDataContext = React.createContext({
    authToken: null,
    permissions: null,
    userData: null,
    setAuthToken: () => {},
    impersonatedOrganization: null,
    impersonatedUser: null,
})

export default function withAuth(ComponentToProtect) {
    return class extends Component {
        constructor(props, context) {
            super(props, context)

            axios.interceptors.response.use(response => {
                if (
                    !this.state.loading &&
                    response.config.url !== '/api/auth/impersonator' &&
                    response.config.url !== '/sso-login' &&
                    response.headers.authorization &&
                    axios.defaults.headers.common.Authorization &&
                    axios.defaults.headers.common.Authorization !==
                        response.headers.authorization
                ) {
                    axios.defaults.headers.common.Authorization =
                        response.headers.authorization
                    this.setState(
                        state => {
                            return {
                                userData: {
                                    ...state.userData,
                                    authToken: null,
                                },
                            }
                        },
                        () => {
                            // Update all sso parameters like permissions.
                            this.componentDidMount()
                        }
                    )
                }

                return response
            })

            this.state = {
                loading: true,
                authorized: true,
                userData: {
                    authToken: null,
                    permissions: null,
                    data: null,
                    impersonatedOrganization: null,
                    impersonatedUser: null,
                    setData: data => {
                        this.setState({
                            userData: { ...this.state.userData, data },
                        })
                    },
                    setPermissions: permissions => {
                        this.setState({
                            userData: { ...this.state.userData, permissions },
                        })
                    },
                    setAuthToken: authToken => {
                        this.setState(
                            { userData: { ...this.state.userData, authToken } },
                            () => {
                                axios.defaults.headers.common.Authorization =
                                    this.state.userData.authToken
                            }
                        )
                    },
                    setImpersonatedOrganization: (
                        authToken,
                        impersonatedOrganization,
                        permissions
                    ) => {
                        this.setState(
                            {
                                userData: {
                                    ...this.state.userData,
                                    authToken,
                                    impersonatedOrganization,
                                    permissions,
                                },
                                loading: true,
                            },
                            () => {
                                axios.defaults.headers.common.Authorization =
                                    authToken
                                this.componentDidMount()
                            }
                        )
                    },
                    setImpersonatedUser: (
                        authToken,
                        impersonatedUser,
                        data,
                        impersonatedOrganization,
                        permissions
                    ) => {
                        this.setState(
                            {
                                userData: {
                                    ...this.state.userData,
                                    authToken,
                                    impersonatedUser,
                                    data,
                                    impersonatedOrganization,
                                    permissions,
                                },
                                loading: true,
                            },
                            () => {
                                axios.defaults.headers.common.Authorization =
                                    this.state.userData.authToken
                                this.componentDidMount()
                            }
                        )
                    },
                },
            }
        }

        componentDidMount() {
            const { authToken, setPermissions, setAuthToken, setData } =
                this.state.userData
            if (authToken) {
                if (this.state.loading) {
                    this.setState({ loading: false })
                }
                return
            }

            axios
                .get('/sso-login')
                .then(res => {
                    const { token } = res.data
                    if (!token) {
                        window.location.href = '/sso-login'
                        return
                    }

                    if (
                        res.data.permissions &&
                        res.data.permissions.length !== 0
                    ) {
                        setPermissions(res.data.permissions)
                    } else {
                        this.setState({ loading: false, authorized: false })
                        return
                    }

                    setAuthToken(res.data.token)

                    if (res.data.user) {
                        const {
                            firstName,
                            lastName,
                            email,
                            username,
                            is_global_admin,
                            is_local_user,
                            organization_id,
                        } = res.data.user
                        setData({
                            firstName,
                            lastName,
                            email,
                            username,
                            is_global_admin,
                            is_local_user,
                            organization_id,
                        })
                        this.setState({ loading: false })
                    }
                })
                .catch(_ => {
                    this.setState({ loading: false, authorized: false })
                })
        }

        render() {
            const { loading, authorized } = this.state
            if (loading) {
                return null
            }
            if (!authorized) {
                return <Redirect to={'/unauthorized'} />
            }

            return (
                <UserDataContext.Provider value={this.state.userData}>
                    <ComponentToProtect {...this.props} />
                </UserDataContext.Provider>
            )
        }
    }
}
